import { useState } from "react";
import { CheckCircle, AlertTriangle, ExternalLink, ChevronDown } from "lucide-react";

export const BrandConsistencySection = () => {
  const [isOpen, setIsOpen] = useState(false);

  const checklistItems = [
    'Is your logo consistent across all social platforms?',
    'Are your handles the same across all social platforms? Or do you use different names across different channels?',
    'Are you listed on Apple Maps, Google Maps, and Google My Business?',
    'How many social channels are you on? Should you add others?',
  ];

  return (
    <div className="relative z-10 mb-8" data-testid="brand-consistency-section">
      <div className="bg-white/[0.03] rounded-2xl border border-white/10 overflow-hidden">
        {/* Trigger */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center gap-4 p-6 text-left hover:bg-white/[0.02] transition-colors"
          data-testid="brand-consistency-toggle"
        >
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 border border-white/10">
            <CheckCircle className="w-6 h-6 text-slate-300" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-white text-xl font-bold">Two Minute Quiz: Your Brand Consistency</p>
              <div className="flex items-center gap-1.5">
                <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/2ogzqkim_facebook.png" alt="Facebook" className="w-5 h-5 rounded object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/e19ae11s_every-social-media-logo-and-icon-in-one-handy-place-instagram-app-logo.webp" alt="Instagram" className="w-5 h-5 rounded object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/8i1w20dn_youtube.png" alt="YouTube" className="w-5 h-5 rounded object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/v1owe0rg_LinkedIn_logo_initials.png" alt="LinkedIn" className="w-5 h-5 rounded object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/7d29huqk_Untitled%20%282%29.png" alt="Google Business" className="w-5 h-5 rounded object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/tr2kocn1_google%20maps%202026.png" alt="Google Maps" className="w-5 h-5 rounded object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/bqyay1eg_sq-google-g-logo-update_dezeen_2364_col_0.jpg" alt="Google" className="w-5 h-5 rounded object-contain" />
              </div>
            </div>
            <p className="text-slate-500 text-sm mt-0.5">Take our free 2-minute brand assessment to find out</p>
          </div>
          <span className="text-sm font-semibold text-slate-400 mr-1">{isOpen ? 'Close' : 'Learn More'}</span>
          <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown content */}
        {isOpen && (
          <div className="px-6 pb-6 pt-0">
            {/* Checklist + CTA side by side */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-4">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Checklist — left, prominent */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-5">
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                    <h4 className="text-white text-lg font-bold">Quick Consistency Checklist</h4>
                  </div>
                  <ul className="space-y-4">
                    {checklistItems.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-200 text-base leading-relaxed">
                        <span className="mt-2 w-2.5 h-2.5 rounded-full bg-emerald-400/70 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA — right, vertically centered */}
                <div className="flex flex-col items-center justify-center gap-4 lg:pl-6 lg:border-l border-white/10">
                  <div className="flex items-center justify-center gap-2.5 flex-wrap">
                    <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/2ogzqkim_facebook.png" alt="Facebook" className="w-8 h-8 rounded-lg object-contain" />
                    <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/e19ae11s_every-social-media-logo-and-icon-in-one-handy-place-instagram-app-logo.webp" alt="Instagram" className="w-8 h-8 rounded-lg object-contain" />
                    <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/8i1w20dn_youtube.png" alt="YouTube" className="w-8 h-8 rounded-lg object-contain" />
                    <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/v1owe0rg_LinkedIn_logo_initials.png" alt="LinkedIn" className="w-8 h-8 rounded-lg object-contain" />
                    <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/7d29huqk_Untitled%20%282%29.png" alt="Google Business" className="w-8 h-8 rounded-lg object-contain" />
                    <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/tr2kocn1_google%20maps%202026.png" alt="Google Maps" className="w-8 h-8 rounded-lg object-contain" />
                    <img src="https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/bqyay1eg_sq-google-g-logo-update_dezeen_2364_col_0.jpg" alt="Google" className="w-8 h-8 rounded-lg object-contain" />
                  </div>
                  <a
                    href="https://brand-report-r-1773343747.emergent.host/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 transition-all hover:scale-[1.02]"
                    data-testid="brand-consistency-cta-main"
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <p className="text-white font-semibold text-sm">Take Quiz now!</p>
                    <ExternalLink className="w-4 h-4 text-slate-500" />
                  </a>
                  <p className="text-slate-600 text-xs">Free • 2 minutes • No signup</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
